/**
 * CreateSessionPage - Multi-step session creation form
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionsService } from '@/services/sessions';
import { classesService } from '@/services/classes';
import { CreateSessionRequest } from '@/types/session';
import { Class, CreateClassRequest, UpdateClassRequest } from '@/types/class';
import { getPlayerIcon } from '@/config/playerIcons';
import { ClassSelector } from '@/components/ClassSelector';
import { CreateClassModal } from '@/components/CreateClassModal';

type FormStep = 1 | 2 | 3;

const DEFAULT_ROLES = ['Biologist', 'Historian', 'Diver', 'Hunter', 'Fixer'];
const REQUIRED_PLAYERS = 5;

export function CreateSessionPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<FormStep>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [classId, setClassId] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [sessionNameTouched, setSessionNameTouched] = useState(false);
  const [isCreateClassModalOpen, setIsCreateClassModalOpen] = useState(false);
  const [classSelectorReloadToken, setClassSelectorReloadToken] = useState(0);
  const [optimisticClass, setOptimisticClass] = useState<Class | null>(null);

  // Validation
  function isSessionNameValid(): boolean {
    return sessionName.trim().length > 0;
  }

  function isClassIdValid(): boolean {
    return classId !== '' && classId !== '__create_new__';
  }

  function canProceedFromStep(step: FormStep): boolean {
    switch (step) {
      case 1:
        return isSessionNameValid() && isClassIdValid();
      case 2:
        return true; // Players step is informational
      default:
        return true;
    }
  }

  async function handleCreateClass(data: CreateClassRequest | UpdateClassRequest) {
    // For create, we need className to be present
    if ('className' in data && data.className) {
      const newClass = await classesService.createClass(data as CreateClassRequest);
      setOptimisticClass(newClass);
      setClassId(newClass.classId);
      setClassSelectorReloadToken((prev) => prev + 1);
    }
  }

  function handleClassChange(value: string) {
    if (value === '__create_new__') {
      setIsCreateClassModalOpen(true);
    } else {
      setClassId(value);
    }
  }

  function handleNext() {
    if (currentStep === 1) {
      setSessionNameTouched(true);
    }
    if (canProceedFromStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 3) as FormStep);
    }
  }

  function handleBack() {
    setCurrentStep((prev) => Math.max(prev - 1, 1) as FormStep);
  }

  async function handleCreate() {
    try {
      setLoading(true);
      setError(null);

      const request: CreateSessionRequest = {
        classId,
        sessionName: sessionName.trim(),
        code: undefined, // Auto-generate
        requiredPlayers: REQUIRED_PLAYERS,
        allowLateJoins: false,
        characterPool: DEFAULT_ROLES,
      };

      const response = await sessionsService.createSession(request);

      // Navigate to session detail page
      navigate(`/login/teacher/sessions/${response.session.sessionId}`);
    } catch (err: any) {
      console.error('Failed to create session:', err);
      setError(err.response?.data?.message || 'Failed to create session. Please try again.');
      setLoading(false);
    }
  }

  function renderStep1() {
    const showNameError = sessionNameTouched && !isSessionNameValid();

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-black mb-2">Basic Information</h2>
          <p className="text-gray-600">Set up your session name</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="sessionName" className="block text-sm font-medium text-black">
            Session Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="sessionName"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            onBlur={() => setSessionNameTouched(true)}
            placeholder="e.g., Morning Class - Mission 1"
            maxLength={100}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-black ${
              showNameError ? 'border-red-500' : 'border-gray-200'
            }`}
          />
          {showNameError ? (
            <small className="block text-sm text-red-600">Session name is required</small>
          ) : (
            <small className="block text-sm text-gray-500">Give your session a memorable name</small>
          )}
        </div>

        {/* Class Selector */}
        <ClassSelector
          value={classId}
          onChange={handleClassChange}
          onCreateNew={() => setIsCreateClassModalOpen(true)}
          error={!isClassIdValid() && sessionNameTouched ? 'Please select a class' : undefined}
          reloadToken={classSelectorReloadToken}
          optimisticClass={optimisticClass}
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-black">
            Join Code
          </label>
          <input
            type="text"
            value="Will be auto-generated"
            disabled
            className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <small className="block text-sm text-gray-500">
            A unique 4-character code will be generated for students to join
          </small>
        </div>
      </div>
    );
  }

  function renderStep2() {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-black mb-2">Players & Roles</h2>
          <p className="text-gray-600">Review the game configuration</p>
        </div>

        {/* Player Count */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">{REQUIRED_PLAYERS}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-black">Total Players</h3>
              <p className="text-sm text-gray-600">This game requires exactly 5 players</p>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-yellow-800">Important</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Students cannot join after you start the session. Make sure all 5 players have joined before starting.
              </p>
            </div>
          </div>
        </div>

        {/* Roles */}
        <div>
          <h3 className="text-lg font-semibold text-black mb-3">Game Roles</h3>
          <p className="text-sm text-gray-600 mb-4">Each player will be randomly assigned one of these roles:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {DEFAULT_ROLES.map((role) => {
              const iconUrl = getPlayerIcon(role);
              return (
                <div
                  key={role}
                  className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-200 rounded-lg"
                >
                  {iconUrl ? (
                    <img src={iconUrl} alt={role} className="w-10 h-10 object-contain" />
                  ) : (
                    <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold">
                      {role.charAt(0)}
                    </div>
                  )}
                  <span className="text-sm font-medium text-black">{role}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  function renderStep3() {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-black mb-2">Review & Create</h2>
          <p className="text-gray-600">Review your session configuration</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm divide-y divide-gray-200">
          <div className="px-6 py-4 flex justify-between items-center">
            <strong className="text-sm font-medium text-black">Session Name:</strong>
            <span className="text-sm text-gray-700">{sessionName}</span>
          </div>

          <div className="px-6 py-4 flex justify-between items-center">
            <strong className="text-sm font-medium text-black">Join Code:</strong>
            <span className="text-sm text-gray-500 italic">Auto-generated on creation</span>
          </div>

          <div className="px-6 py-4 flex justify-between items-center">
            <strong className="text-sm font-medium text-black">Players Required:</strong>
            <span className="text-sm text-gray-700">{REQUIRED_PLAYERS}</span>
          </div>

          <div className="px-6 py-4 flex justify-between items-center">
            <strong className="text-sm font-medium text-black">Late Joins:</strong>
            <span className="text-sm text-gray-700">Not allowed</span>
          </div>

          <div className="px-6 py-4 flex justify-between items-center">
            <strong className="text-sm font-medium text-black">Character Pool:</strong>
            <span className="text-sm text-gray-700">{DEFAULT_ROLES.join(', ')}</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-800">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-black">Create New Session</h1>
          <button
            type="button"
            onClick={() => navigate('/login/teacher')}
            className="text-gray-600 hover:text-black transition-colors"
          >
            Cancel
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex justify-between items-center mb-12">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className="flex flex-col items-center"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                currentStep >= step
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-500'
              } ${currentStep === step ? 'ring-4 ring-blue-100' : ''}`}>
                {step}
              </div>
              <div className={`text-sm font-medium ${
                currentStep >= step ? 'text-black' : 'text-gray-500'
              }`}>
                {step === 1 && 'Basic Info'}
                {step === 2 && 'Players'}
                {step === 3 && 'Review'}
              </div>
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-2 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {currentStep < 3 && (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canProceedFromStep(currentStep)}
            >
              Next
            </button>
          )}

          {currentStep === 3 && (
            <button
              type="button"
              onClick={handleCreate}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Session'}
            </button>
          )}
        </div>
      </div>

      {/* Create Class Modal */}
      <CreateClassModal
        isOpen={isCreateClassModalOpen}
        onClose={() => setIsCreateClassModalOpen(false)}
        onSubmit={handleCreateClass}
      />
    </div>
  );
}
